import { assertKitchenResponse } from "./kitchenActions";
import { findTaskCartProduct, getCartProducts } from "./preparationComments";

const terminalStatuses = ["COMPLETED", "READY", "REMOVED", "REJECTED", "CANCELLED", "CANCELED"];
export const cancellableItems = (order) => (order?.items || []).filter((item) =>
  ![item.taskStatus, item.status].some((status) =>
    terminalStatuses.includes(String(status || "").toUpperCase())
  ) && Number.isFinite(Number(item.qty)) && Number(item.qty) > 0);

export const loadCancellationContext = async (order, loadCart) => {
  const cartId = order?.cartId || order?.metadata?.cartId;
  if (!cartId) return { cartId: null, products: [], unpaid: false, paid: false };
  const response = await loadCart(cartId);
  const products = getCartProducts(response);
  const cart = response.data.data.data;
  const payment = String(cart.paymentStatus || cart.baseOrderResponse?.paymentStatus || "").toUpperCase();
  return {
    cartId, products,
    unpaid: ["PAYMENT_PENDING", "UNPAID", "NOT_PAID"].includes(payment),
    paid: ["COMPLETED", "PAID", "PARTIALLY_PAID"].includes(payment),
  };
};

/** Preflight every selected line before making any cancellation request. */
export const cancelKitchenOrderItems = async ({
  order, taskIds, reason, markWastage = false, doNotCharge = false,
  loadCart, recall, rejectJobs, audit,
}) => {
  const eligible = cancellableItems(order);
  const selected = eligible.filter((item) => taskIds.includes(item.taskId));
  if (!selected.length || selected.length !== new Set(taskIds).size) {
    throw new Error("Select current items to cancel. Refresh if the order has changed.");
  }
  if (!String(reason || "").trim()) throw new Error("Choose a cancellation reason.");
  const context = await loadCancellationContext(order, loadCart);
  if (!context.cartId) {
    if (selected.length !== eligible.length || markWastage || doNotCharge) {
      throw new Error("This order has no linked cart. Only whole-order cancellation without billing or wastage changes is available.");
    }
    assertKitchenResponse(await rejectJobs({
      ...audit, jobIds: [order.id], status: "REJECTED", reason,
    }));
    return;
  }
  if (doNotCharge && !context.unpaid) {
    throw new Error("The cart is not confirmed unpaid. Billing changes and refunds were not processed.");
  }
  const job = {
    tasks: eligible.map((item) => ({ ...item, itemId: item.id })),
  };
  const plans = new Map();
  selected.forEach((item) => {
    if (!item.taskId) throw new Error("This item is missing its kitchen task identifier.");
    const product = findTaskCartProduct({ ...item, itemId: item.id }, job, context.products);
    if (!product) throw new Error(`Cannot safely match ${item.name || "item"} to its cart product. Nothing was cancelled.`);
    const id = product.cartProductId;
    const existing = plans.get(id) || { product, quantity: 0 };
    existing.quantity += Number(item.qty);
    plans.set(id, existing);
  });
  plans.forEach(({ product, quantity }) => {
    const available = Number(product.quantity ?? product.qty);
    if (!Number.isFinite(available) || quantity > available) {
      throw new Error("The cart quantities have changed. Refresh before cancelling; nothing was cancelled.");
    }
  });
  let completed = 0;
  try {
    for (const [cartProductId, { quantity }] of plans) {
      assertKitchenResponse(await recall({
        cartId: context.cartId, cartProductId, quantity,
        reason: markWastage ? "WASTAGE" : "RETURNED",
        reasonDescription: reason, chargeApplicable: !doNotCharge,
        updatedBy: audit.updatedBy, comments: "",
      }));
      completed += 1;
    }
  } catch (error) {
    if (completed) {
      const partial = new Error(`${completed} item cancellation(s) succeeded, but the remaining items could not be cancelled. Refresh before retrying.`);
      partial.partialCancellation = true;
      throw partial;
    }
    throw error;
  }
};
